import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { type UseFormRegisterReturn, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { z } from "zod";

import { AdminIcon } from "@/components/admin/icon";
import { AuthBrand } from "@/components/auth/auth-brand";
import { extractErrorMessage, useLoginMutation } from "@/hooks/use-auth-api";
import { useAuthStore } from "@/stores/auth.store";

const loginSchemaEmail = z.object({
	email: z.string().email("Adresse e-mail invalide"),
	password: z.string().min(1, "Mot de passe requis"),
	remember: z.boolean().optional(),
});

type LoginEmailValues = z.infer<typeof loginSchemaEmail>;

export function LoginPage() {
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
	const setPending2FA = useAuthStore((s) => s.setPending2FA);
	const setSession = useAuthStore((s) => s.setSession);
	const navigate = useNavigate();
	const [showPwd, setShowPwd] = useState(false);
	const [globalError, setGlobalError] = useState<string | null>(null);
	const loginMutation = useLoginMutation();

	if (isAuthenticated) return <Navigate to="/" replace />;

	const handleEmailSubmit = async (values: LoginEmailValues) => {
		setGlobalError(null);
		try {
			const res = await loginMutation.mutateAsync({
				email: values.email,
				password: values.password,
			});
			if (res.status === "needs-2fa" && res.pendingToken) {
				setPending2FA({
					email: values.email,
					token: res.pendingToken,
					methods: res.methods ?? [],
				});
				navigate("/login/2fa");
			} else if (
				res.status === "authenticated" &&
				res.user &&
				res.accessToken &&
				res.refreshToken
			) {
				setSession({
					user: res.user,
					accessToken: res.accessToken,
					refreshToken: res.refreshToken,
				});
				navigate("/");
			} else {
				setGlobalError("Réponse inattendue du serveur");
			}
		} catch (err) {
			setGlobalError(extractErrorMessage(err, "Identifiants invalides"));
		}
	};

	return (
		<div className="auth-page">
			<AuthBrand />
			<main className="auth-panel">
				<div className="auth-form-wrap">
					<div className="auth-h">
						<h2>Connexion administrateur</h2>
						<p>Accédez à votre tableau de bord Akwaba.</p>
					</div>

					{globalError && (
						<div
							className="auth-alert danger"
							role="alert"
							data-testid="login-error"
						>
							<span className="icon">
								<AdminIcon n="alert" s={16} />
							</span>
							<div>
								<strong>Connexion impossible</strong>
								{globalError}
							</div>
						</div>
					)}

					{/* L'onglet « Téléphone » menait à un message « phase 2 » : un
					    chemin proposé qui n'aboutit nulle part. La connexion par mot
					    de passe est de toute façon la seule retenue pour le
					    back-office. */}
					<EmailForm
						showPwd={showPwd}
						onTogglePwd={() => setShowPwd((v) => !v)}
						onSubmit={handleEmailSubmit}
						loading={loginMutation.isPending}
					/>

					{/* Ces trois entrées étaient des liens sans destination : ils
					    prenaient l'apparence et le curseur d'un lien, et ne menaient
					    nulle part. Les mentions légales pointent vers la page publique,
					    qui existe ; le reste redevient du texte. */}
					<div className="auth-foot">
						Pas de compte administrateur ? Contactez votre responsable IT.
					</div>
					<div className="auth-foot-meta">
						<a
							href="https://akwaba.cm/fr/cgv"
							target="_blank"
							rel="noreferrer noopener"
						>
							Mentions légales
						</a>
						<span>Akwaba Commerce SA</span>
					</div>
				</div>
			</main>
		</div>
	);
}

interface EmailFormProps {
	showPwd: boolean;
	onTogglePwd: () => void;
	onSubmit: (values: LoginEmailValues) => void;
	loading: boolean;
}

function EmailForm({
	showPwd,
	onTogglePwd,
	onSubmit,
	loading,
}: EmailFormProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginEmailValues>({
		resolver: zodResolver(loginSchemaEmail),
		defaultValues: { email: "", password: "", remember: true },
	});

	return (
		<form onSubmit={handleSubmit(onSubmit)} noValidate>
			<div className="field">
				<div className="field-label">Email professionnel</div>
				<div className={"field-input-wrap " + (errors.email ? "error" : "")}>
					<span className="input-prefix">
						<AdminIcon n="mail" s={16} c="var(--a-ink-mute)" />
					</span>
					<input
						type="email"
						placeholder="vous@akwaba.cm"
						autoFocus
						autoComplete="email"
						data-testid="login-email"
						{...register("email")}
					/>
				</div>
				{errors.email && (
					<div className="field-help error">
						<AdminIcon n="alert" s={12} />
						{errors.email.message}
					</div>
				)}
			</div>

			<PasswordField
				register={register("password")}
				error={errors.password?.message}
				showPwd={showPwd}
				onTogglePwd={onTogglePwd}
			/>

			<div className="field-row">
				<label className="checkbox">
					<input type="checkbox" {...register("remember")} />
					<span>Garder cette session 7 jours</span>
				</label>
				<span style={{ fontSize: 11.5, color: "var(--a-ink-mute)" }}>
					IP locale · Chrome
				</span>
			</div>

			<button
				type="submit"
				className="btn-submit"
				disabled={loading}
				data-testid="login-submit"
			>
				{loading ? "Vérification…" : "Continuer"}
				<AdminIcon n="arrow" s={16} c="#fff" />
			</button>

		</form>
	);
}


interface PasswordFieldProps {
	register: UseFormRegisterReturn;
	error?: string;
	showPwd: boolean;
	onTogglePwd: () => void;
}

function PasswordField({
	register,
	error,
	showPwd,
	onTogglePwd,
}: PasswordFieldProps) {
	return (
		<div className="field">
			<div className="field-label">
				Mot de passe
				<a href="/login/mot-de-passe-oublie">Mot de passe oublié ?</a>
			</div>
			<div className={"field-input-wrap " + (error ? "error" : "")}>
				<span className="input-prefix">
					<AdminIcon n="lock" s={16} c="var(--a-ink-mute)" />
				</span>
				<input
					type={showPwd ? "text" : "password"}
					placeholder="••••••••••••"
					autoComplete="current-password"
					data-testid="login-password"
					{...register}
				/>
				<button
					type="button"
					className="input-suffix"
					onClick={onTogglePwd}
					tabIndex={-1}
				>
					<AdminIcon n={showPwd ? "eyeOff" : "eye"} s={16} />
				</button>
			</div>
			{error && (
				<div className="field-help error">
					<AdminIcon n="alert" s={12} />
					{error}
				</div>
			)}
		</div>
	);
}
